import React, { useState, useEffect, useRef } from 'react';
import { Card, Typography, Flex, Space, Button, Alert } from 'antd';
import GerenciadorDePerfis from 'pages/Usuarios/Components/GerenciadorDePerfis';
import DireitosPortalService from 'services/DireitosPortalService';
import LoadingOrErrorPanel from 'components/LoadingOrErrorPanel';
import GerenciadorDeLicencas from 'pages/Usuarios/Components/GerenciadorDeLicencas';
import Policy from 'EstruturaAntiga/pages/usuariosV2/componentes/policy';
import policiesObject from '../../../../EstruturaAntiga/pages/Provisionamento/polices.json';
import PerfisDeAcessoService from 'services/PerfisDeAcessoService';
import { useAuth } from 'context/AuthContext';
import UsuariosService from 'services/UsuariosService';
import { ModalErro } from 'components/Modais/ModalErro';
import { ModalSucesso } from 'components/Modais/ModalSucesso';
import STEPS from '../Constants/steps';
import BotaoCancelamento from './BotaoCancelamento';
import { useRoteiro } from 'context/RoteiroContext';
import { useGerenciadorDeLicencas } from 'hooks/useGerenciadorDeLicencas';

const { Title, Text } = Typography;

function ConfirmacaoOperacao({ logOperacao, stepAtual, setStepAtual, obterUltimoLog, cancelarOperacao }) {
    const [loading, setLoading] = useState(false);
    const [direitos, setDireitos] = useState([]);
    const [perfisAcesso, setPerfisAcesso] = useState([]);
    const [perfisAcessoAoDireitoAdicionados, setPerfisAcessoAoDireitoAdicionados] = useState([]);
    const [termoAceito, setTermoAceito] = useState(false);
    const [erro, setErro] = useState({temErro: false, mensagens: ''});
    const {setReferenciaObjetosVisuais} = useRoteiro();
    const refBotaoProximaEtapa = useRef(null);
    const refs = [
        { id: 'refCriacaoMultiplosUsuariosBotaoProximaEtapaConfiguracao', ref: refBotaoProximaEtapa }
    ];

    const {tenant, contato, direito} = useAuth();

    const QUANTIDADE_USUARIOS = JSON.parse(logOperacao.tags).QuantidadeUsuarios || 1;
    const { gerenciadorProps, estado } = useGerenciadorDeLicencas({
        planoInicial: null,
        licencasIniciais: [],
        quantidadePlanoSolicitada: QUANTIDADE_USUARIOS
    });

    useEffect(()=>{
        inicializarPagina();
    }, []);

    const inicializarPagina = async () => {
        configurarDireitos();

        configurarPerfis();

        setReferenciaObjetosVisuais(refs);
    }

    const configurarDireitos = async () => {
        const direitosGestao = await DireitosPortalService.ListarDireitosGestaoUsuario()
            .catch(error =>{
                setErro({
                    temErro: true, 
                    mensagens: error.response?.data?.erros ?? 'Erro inesperado ao listar direitos.'
                });
                return;
            });
        setDireitos(direitosGestao);
    }

    const configurarPerfis = async () => {
        const perfis = await PerfisDeAcessoService.ListarTiposPerfisDeAcessoAoDireito(tenant.formId)
            .catch(error =>{
                setErro({
                    temErro: true, 
                    mensagens: error.response?.data?.erros ?? 'Erro inesperado ao listar os perfis de acesso.'
                });
                return;
            });
        setPerfisAcesso(perfis);
    }

    const desabilitarMudancaEtapa = () => {
        const nenhumPerfilAdicionado = perfisAcessoAoDireitoAdicionados.length === 0;
        if (nenhumPerfilAdicionado || !estado.planoDisponivel || loading) {
            return true;
        }
        return false;
    }

    const confirmarOperacao = async () => {
        setLoading(true);
        
        var data = {
            solicitanteId: contato.idContact,
            clienteId: direito.accountId,
            direitoId: direito.entitlementId,
            tenantId: direito.axtTenantId,
            logOperacaoId: logOperacao.logOperacaoId,
            perfisDeAcessoAosDireitos: perfisAcessoAoDireitoAdicionados.map(
                perfilAcesso => {
                    return {
                        DireitoId: perfilAcesso.direito.value,
                        PerfilDeAcesso: perfilAcesso.perfil.value.toString()
                    } 
                }
            ),
            produtoPrincipal: {
                ProdutoPrincipalId: estado.planoSelecionado,
                UnidadePrincipalId: estado.planos.find(p => p.value === estado.planoSelecionado)?.unidadeId 
            },
            produtosOpcionais: estado.licencasSelecionadas.map(
                licenca => {
                    return {
                        ProdutoId: licenca,
                        UnidadeId: estado.licencasAdicionais.find(l => l.value === licenca)?.unidadeId
                    }
                }
            ),
            termoContratoAceito: JSON.stringify(policiesObject)
        }

        await UsuariosService.ProcessarPedidoCriacaoMultiplosUsuarios(data)
            .then(async response => {
                ModalSucesso('Sucesso!', response);
                await obterUltimoLog();
            })
            .catch(error => {
                ModalErro('Erro!', error.response?.data?.erros[0] ?? ['Erro inesperado ao enviar pedido']);
            })
            .finally(() => {
                setLoading(false);
            });
    }  

    return (
        <LoadingOrErrorPanel loading={loading} hasError={erro.temErro} mensagemErro={erro.mensagens}>
            <Card>
                {stepAtual === STEPS.CONFIGURACAO && (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Alert
                            style={{ marginTop: 16 }}
                            message={<strong>Selecione um perfil de acesso e um plano de e-mail para continuar.</strong>}
                            description="As opções escolhidas serão aplicadas a todos os usuários deste cadastro. Após a criação, será possível ajustar individualmente conforme necessário."
                            type="warning"
                            showIcon
                        />

                        <Title level={5}>Planos e licenças</Title>
                        
                        <GerenciadorDeLicencas {...gerenciadorProps} />

                        <GerenciadorDePerfis
                            direitos={direitos}
                            perfisAcesso={perfisAcesso}
                            perfisAcessoAoDireitoAdicionados={perfisAcessoAoDireitoAdicionados}
                            setPerfisAcessoAoDireitoAdicionados={setPerfisAcessoAoDireitoAdicionados}
                        />

                        <Flex justify="end" gap="small">
                            <BotaoCancelamento
                                cancelarOperacao={cancelarOperacao}
                            />
                            <Button type="primary" onClick={() => setStepAtual(STEPS.CONFIRMACAO)} disabled={desabilitarMudancaEtapa()} ref={refBotaoProximaEtapa}>
                                Próxima etapa
                            </Button>
                        </Flex>
                    </Space>
                )}

                {stepAtual === STEPS.CONFIRMACAO && (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Card title="Resumo da operação">

                            <div>
                                <Title level={5} style={{marginTop: 0}}>Perfis de acesso:</Title>
                                {perfisAcessoAoDireitoAdicionados.length > 0 ? (
                                    perfisAcessoAoDireitoAdicionados.map((perfil, index) => (
                                        <Flex>
                                            <Text key={index}>
                                                - {perfil.direito.label} / {perfil.perfil.label}
                                            </Text>
                                        </Flex>
                                    ))
                                ) : (
                                    <Text type="secondary">Nenhum perfil selecionado</Text>
                                )}
                            </div>

                            <div>
                                <Title level={5}>Plano de e-mail:</Title>
                                {estado.planoSelecionado ? (
                                    <Text>
                                        {estado.planos.find(p => p.value === estado.planoSelecionado)?.label}
                                    </Text>
                                ) : (
                                    <Text type="secondary">Nenhum plano selecionado</Text>
                                )}
                            </div>

                            <div>
                                <Title level={5}>Licenças opcionais:</Title>
                                {estado.licencasSelecionadas.length > 0 ? (
                                    estado.licencasSelecionadas.map((licencaId, index) => {
                                        const licenca = estado.licencasAdicionais.find(l => l.value === licencaId);
                                        return (
                                            <Flex>
                                                <Text key={index}>
                                                    - {licenca.label}
                                                </Text>
                                            </Flex>
                                        );
                                    })
                                ) : (
                                    <Text type="secondary">Nenhuma licença selecionada</Text>
                                )}
                            </div>

                            <div>
                                <Title level={5}>Resumo:</Title>
                                <Flex vertical>
                                    <Text>
                                        Quantidade de usuários: {QUANTIDADE_USUARIOS}
                                    </Text>
                                    <Text>
                                        Valor total: R$ {estado.valorTotal.toFixed(2).replace('.', ',')}
                                    </Text>
                                </Flex>
                            </div>
                        </Card>

                        <Policy
                            policies={policiesObject}
                            checkedPolicy={termoAceito}
                            setCheckedPolicy={setTermoAceito}
                        />

                        <Flex justify="space-between">
                            <Button
                                onClick={() => setStepAtual(STEPS.CONFIGURACAO)}
                                disabled={desabilitarMudancaEtapa()}
                            >
                                Voltar
                            </Button>
                            <Flex justify="end" gap="small">
                                <BotaoCancelamento
                                    cancelarOperacao={cancelarOperacao}
                                />
                                <Button
                                    type="primary"
                                    onClick={confirmarOperacao}
                                    disabled={desabilitarMudancaEtapa() || !termoAceito}
                                >
                                    Confirmar
                                </Button>
                            </Flex>
                        </Flex>
                    </Space>                   
                )}
            </Card>
        </LoadingOrErrorPanel>
    );
}

export default ConfirmacaoOperacao;